/**
 * The one boundary between matching-server's raw WebSocket messages and the
 * typed `MatchingSessionState` the UI reads.
 *
 * matching-server sends a full Orchestrator snapshot on connect
 * (`cloneOrchestrator`), then partial messages:
 *   - `{pid: <orchestrator pid>, config}`      — a config echo after set_config
 *   - `{pid: <orchestrator pid>, started}`     — the run has started
 *   - `{pid: <orchestrator pid>, data, K}`     — a column was added
 *   - an InterfaceConductor snapshot           — one matching task changed
 * Anything else (e.g. `{pid, matchings}` from the legacy editor) is ignored.
 */
import { upgradeConfig } from './defaults';
import type {
  Coach,
  Column,
  ColumnKind,
  MatchingConfig,
  MatchingSessionState,
  Participant,
  RunTask,
  RunTaskStatus,
  SlotId,
  SlotResponse,
} from './types';

// ---------------------------------------------------------------------------
// Raw shapes (only the fields we read)
// ---------------------------------------------------------------------------

export type RawParticipant = Record<string, unknown> & { id: string };

export interface RawCoach {
  id: string;
  first_name?: string | null;
  last_name?: string | null;
  preferred_name?: string | null;
  email?: string | null;
  confirmed_time_slots?: string[] | null;
  requested_time_slots?: string[] | null;
  max_groups?: number | null;
  /** The availability request's status: pending, confirmed, declined, expired, cancelled, superseded. */
  status?: string | null;
  slot_availability?: Record<string, { status?: string } | null> | null;
}

export interface RawCoachingConstraints {
  data: {
    id: string;
    available_time_slots: string[];
    max_groups: number | null;
  }[];
}

export interface RawTask {
  pid: string;
  path?: string[];
  task_types?: string[];
  ans?: false | { rep_name?: string } | null;
  awaiting?: boolean;
  constructing?: boolean;
  errored?: boolean;
  error?: unknown;
  exited?: boolean;
  exitCode?: number;
  orchestrung?: RawTask[];
}

export interface RawOrchestratorSnapshot {
  pid: string;
  session_id: string;
  cohort_id?: string;
  timestamp?: string;
  data: RawParticipant[];
  K: string[];
  sz?: number[];
  SLOTS?: string[];
  coach_pool?: RawCoach[];
  CC?: RawCoachingConstraints | null;
  orchestrung?: RawTask[];
  config?: MatchingConfig | null;
  started?: boolean;
}

// ---------------------------------------------------------------------------
// Columns
// ---------------------------------------------------------------------------

/** Keys shown in the fixed columns, or never useful as a matching column. */
const NON_ATTRIBUTE_KEYS = new Set([
  'id',
  'email',
  'first_name',
  'last_name',
  'preferred_name',
  'bio',
  'available_time_slots',
  'dnm',
  'dnm_groupings',
  'availability',
]);

export function humanizeKey(key: string): string {
  const spaced = key.replace(/^cf_/, '').replace(/[_-]+/g, ' ').trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function columnKind(values: unknown[]): ColumnKind {
  const present = values.filter(v => v !== null && v !== undefined && v !== '');
  if (present.some(v => Array.isArray(v))) return 'multi';
  if (
    present.length > 0 &&
    present.every(
      v =>
        typeof v === 'number' ||
        (typeof v === 'string' && v.trim() !== '' && !Number.isNaN(Number(v)))
    )
  ) {
    return 'number';
  }
  return 'single';
}

function deriveColumns(rows: RawParticipant[], keys: string[]): Column[] {
  return keys
    .filter(k => !NON_ATTRIBUTE_KEYS.has(k))
    .filter(k => {
      // A column where every person has their own value (names, bios) can't
      // shape groups, so it isn't offered.
      const values = rows
        .map(r => r[k])
        .filter(v => v !== null && v !== undefined && v !== '');
      const distinct = new Set(values.map(v => JSON.stringify(v)));
      return rows.length <= 1 || distinct.size < rows.length;
    })
    .map(k => ({
      key: k,
      label: humanizeKey(k),
      kind: columnKind(rows.map(r => r[k])),
    }));
}

// ---------------------------------------------------------------------------
// Participants and coaches
// ---------------------------------------------------------------------------

function str(v: unknown): string {
  return typeof v === 'string' ? v.trim() : '';
}

function splitList(v: unknown): string[] {
  if (Array.isArray(v)) return v.map(x => String(x).trim()).filter(Boolean);
  return str(v)
    .split(/[,;]+/)
    .map(x => x.trim())
    .filter(Boolean);
}

function personName(r: Record<string, unknown>): string {
  const first = str(r.preferred_name) || str(r.first_name);
  const full = `${first} ${str(r.last_name)}`.trim();
  return full || str(r.email) || String(r.id);
}

function toAttribute(v: unknown): string | string[] | null {
  if (v === null || v === undefined || v === '') return null;
  if (Array.isArray(v)) return v.map(x => String(x));
  return String(v);
}

function normalizeParticipants(
  rows: RawParticipant[],
  columns: Column[]
): Participant[] {
  const idByEmail = new Map<string, string>();
  for (const r of rows) {
    const email = str(r.email).toLowerCase();
    if (email) idByEmail.set(email, r.id);
  }
  const ids = new Set(rows.map(r => r.id));
  return rows.map(r => {
    // matching-server turns each `dnm` email into the participant's id and
    // keeps the emails it can't match as-is. Older payloads carry only emails.
    const recognized = new Set<string>();
    const unrecognized = new Set<string>();
    for (const x of splitList(r.dnm)) {
      const id = ids.has(x) ? x : idByEmail.get(x.toLowerCase());
      if (id === undefined) unrecognized.add(x);
      else if (id !== r.id) recognized.add(id);
    }
    return {
      id: r.id,
      name: personName(r),
      email: str(r.email),
      sourceSlots: Array.isArray(r.available_time_slots)
        ? r.available_time_slots.map(String)
        : [],
      sourceDoNotMatch: [...recognized],
      sourceUnrecognizedDoNotMatch: [...unrecognized],
      availabilityNote: str(r.availability),
      attributes: Object.fromEntries(
        columns.map(c => [c.key, toAttribute(r[c.key])])
      ),
    };
  });
}

const SLOT_RESPONSES = new Set<SlotResponse>([
  'full',
  'partial',
  'unavailable',
]);

function slotResponses(
  raw: RawCoach['slot_availability']
): Record<SlotId, SlotResponse> {
  const out: Record<SlotId, SlotResponse> = {};
  for (const [slot, answer] of Object.entries(raw ?? {})) {
    const status = answer?.status as SlotResponse | undefined;
    if (status && SLOT_RESPONSES.has(status)) out[slot] = status;
  }
  return out;
}

/**
 * Starting availability: nothing while the request is pending; otherwise
 * the per-slot answers (full or partial = available), falling back to the
 * confirmed slots when there are no per-slot answers.
 */
function coachStartingSlots(
  c: RawCoach,
  responses: Record<SlotId, SlotResponse>
): SlotId[] {
  if (c.status === 'pending') return [];
  const answered = Object.entries(responses);
  if (answered.length > 0)
    return answered
      .filter(([, r]) => r !== 'unavailable')
      .map(([slot]) => slot);
  return c.confirmed_time_slots ?? [];
}

function normalizeCoaches(
  pool: RawCoach[] | undefined,
  cc: RawCoachingConstraints | null | undefined
): Coach[] {
  const override = new Map((cc?.data ?? []).map(c => [c.id, c]));
  return (pool ?? []).map(c => {
    const o = override.get(c.id);
    const responses = slotResponses(c.slot_availability);
    return {
      id: c.id,
      name: personName(c as unknown as Record<string, unknown>),
      sourceSlots: o
        ? o.available_time_slots
        : coachStartingSlots(c, responses),
      sourceMaxGroups: o ? (o.max_groups ?? null) : (c.max_groups ?? null),
      respondedMaxGroups:
        typeof c.max_groups === 'number' ? c.max_groups : null,
      requestStatus: c.status ?? null,
      awaitingResponse: c.status === 'pending',
      requestedSlots: Array.isArray(c.requested_time_slots)
        ? c.requested_time_slots
        : null,
      slotResponses: responses,
    };
  });
}

// ---------------------------------------------------------------------------
// Tasks
// ---------------------------------------------------------------------------

/** Worker errors arrive as strings or as serialized Error objects. */
export function errorText(error: unknown): string | null {
  if (!error) return null;
  if (typeof error === 'string') return error;
  if (
    typeof error === 'object' &&
    'message' in error &&
    typeof error.message === 'string'
  )
    return error.message;
  try {
    return JSON.stringify(error);
  } catch {
    return 'Unknown error';
  }
}

/**
 * Node reports exit code 1 for `worker.terminate()`, which matching-server
 * calls on purpose: on a finished task once the search moves past it, and
 * during memory wind-down. Only other exit codes are crashes. Same rule as
 * the legacy UI (`UInterfaceConductor` status getter).
 */
const TERMINATED_EXIT_CODE = 1;

export function taskStatus(t: RawTask): RunTaskStatus {
  if (t.errored || (t.exited && t.exitCode !== TERMINATED_EXIT_CODE))
    return 'error';
  if (t.awaiting || t.constructing) return 'running';
  if (t.ans === false) return 'dead_end';
  if (t.ans && typeof t.ans === 'object' && t.ans.rep_name) return 'found';
  // Terminated before it had an answer: the search no longer needs it.
  if (t.exited) return 'stopped';
  return 'idle';
}

function toTask(t: RawTask): RunTask {
  return {
    pid: t.pid,
    path: t.path ?? [t.pid],
    taskTypes: t.task_types ?? [],
    status: taskStatus(t),
    error: errorText(t.error),
  };
}

function flattenTasks(
  tasks: RawTask[] | undefined,
  out: Record<string, RunTask> = {}
): Record<string, RunTask> {
  for (const t of tasks ?? []) {
    out[t.pid] = toTask(t);
    flattenTasks(t.orchestrung, out);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export function isOrchestratorSnapshot(
  msg: unknown
): msg is RawOrchestratorSnapshot {
  const m = msg as Partial<RawOrchestratorSnapshot> | null;
  return (
    !!m &&
    typeof m.pid === 'string' &&
    Array.isArray(m.data) &&
    Array.isArray(m.K) &&
    'orchestrung' in m
  );
}

export function normalizeSnapshot(
  raw: RawOrchestratorSnapshot
): MatchingSessionState {
  const columns = deriveColumns(raw.data, raw.K);
  const tasks = flattenTasks(raw.orchestrung);
  const slots = raw.SLOTS?.length
    ? raw.SLOTS
    : [
        ...new Set(
          raw.data.flatMap(r =>
            Array.isArray(r.available_time_slots)
              ? r.available_time_slots.map(String)
              : []
          )
        ),
      ];
  return {
    sessionId: raw.session_id,
    cohortId: raw.cohort_id ?? null,
    dataAsOf: raw.timestamp ?? null,
    participants: normalizeParticipants(raw.data, columns),
    columns,
    slots,
    coaches: normalizeCoaches(raw.coach_pool, raw.CC),
    defaultSizes: raw.sz ?? [],
    config: raw.config ? upgradeConfig(raw.config) : null,
    // Sessions started from the legacy UI have tasks but no `started` flag.
    phase: raw.started || Object.keys(tasks).length > 0 ? 'running' : 'setup',
    tasks,
  };
}

/**
 * Fold one incoming message into the session. `raw` is kept alongside so a
 * column patch can re-derive participants from the full rows.
 */
export function applyMessage(
  current: { state: MatchingSessionState; raw: RawOrchestratorSnapshot } | null,
  msg: unknown
): { state: MatchingSessionState; raw: RawOrchestratorSnapshot } | null {
  if (isOrchestratorSnapshot(msg))
    return { state: normalizeSnapshot(msg), raw: msg };
  if (!current || !msg || typeof msg !== 'object') return current;

  const m = msg as Record<string, unknown> & { pid?: string };
  if (m.pid === current.raw.pid) {
    const raw: RawOrchestratorSnapshot = { ...current.raw };
    if ('config' in m) raw.config = (m.config as MatchingConfig | null) ?? null;
    if ('started' in m) raw.started = Boolean(m.started);
    if (Array.isArray(m.data) && Array.isArray(m.K)) {
      raw.data = m.data as RawParticipant[];
      raw.K = m.K as string[];
    }
    if ('CC' in m) raw.CC = m.CC as RawCoachingConstraints | null;
    const state = normalizeSnapshot({ ...raw, orchestrung: [] });
    return {
      state: {
        ...state,
        tasks: current.state.tasks,
        phase:
          raw.started || Object.keys(current.state.tasks).length > 0
            ? 'running'
            : 'setup',
      },
      raw,
    };
  }

  if (typeof m.pid === 'string' && Array.isArray(m.path)) {
    const task = toTask(m as unknown as RawTask);
    return {
      raw: current.raw,
      state: {
        ...current.state,
        phase: 'running',
        tasks: { ...current.state.tasks, [task.pid]: task },
      },
    };
  }

  return current;
}
